import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { of } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../shared/toastr.service";
import {
  CHANGE_EXCLUDED_FROM_SIGNATURES,
  ChangeExcludedFromSignaturesAction,
  ChangeExcludedFromSignaturesSuccessAction,
  EXCLUDE_ALL_EMPLOYEES,
  ExcludeAllEmployeesAction,
  GET_EMPLOYEES,
  GET_EMPLOYEES_SUCCESS,
  GET_EMPLOYEES_FAILURE,
  GetEmployeesAction,
  GetEmployeesFailureAction,
  GetEmployeesSuccessAction,
  SET_EMPLOYEES_PARAMS,
  SetEmployeesParamsAction,
  SetLoadingAction,
  ChangeExcludedFromSignaturesFailureAction,
  SEND_EMPLOYEE_REQUEST,
  SendEmployeeRequestSuccessAction,
  SendEmployeeRequestFailureAction,
  SendEmployeeRequestAction,
  GET_EMPLOYEE,
  GetEmployeeAction,
  GetEmployeeSuccessAction,
  GetEmployeeFailureAction,
  UPDATE_EMPLOYEE,
  UpdateEmployeeAction,
  UpdateEmployeeSuccessAction,
  UpdateEmployeeFailureAction,
  UPDATE_EMPLOYEE_SUCCESS,
  GET_CURRENT_USER,
  GetCurrentUserSuccessAction,
  GetCurrentUserFailureAction,
  UpdateEmployeeAndInstallSignatureAction,
  UPDATE_EMPLOYEE_AND_INSTALL_SIGNATURE,
  UpdateEmployeeAndInstallSignatureSuccessAction,
  UpdateEmployeeAndInstallSignatureFailureAction,
  UpdateCurrentUserAction,
  UPDATE_CURRENT_USER,
  UpdateCurrentUserSuccessAction,
  UpdateCurrentUserFailureAction,
  RemoveSignatureTemplateAction,
  RemoveSignatureTemplateSuccessAction,
  RemoveSignatureTemplateFailureAction,
  REMOVE_SIGNATURE_TEMPLATE,
  SYNC_GOOGLE_EMPLOYEES,
  SyncGoogleEmployeesFailureAction,
  SyncGoogleEmployeesSuccessAction,
  SYNC_MICROSOFT_EMPLOYEES,
  SyncMicrosoftEmployeesFailureAction,
  SyncMicrosoftEmployeesSuccessAction,
  CLEAR_EMPLOYEES_PARAMS,
  GetCurrentUserAction,
} from "./employees.actions";
import { StartCheckingForInstallationInProgressAction } from "../signature-installation-state/signature-installation-state.actions";
import { getEmployeesParamsSelector } from "./employees.reducer";
import { EmployeesService } from "./employees.service";
import { PaginatedData } from "../shared/model/table.interface";
import {
  ChangeExcludedFromSignaturesParams,
  Employee,
  EmployeeListItem,
  EmployeeListSearchParams,
  EmployeeRequest,
  SingleEmployeeResponse,
  CurrentUserResponse,
} from "./employees.interface";
import { AuthService } from "../auth/auth.service";

@Injectable()
export class EmployeesEffects {
  getEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetEmployeesAction>(GET_EMPLOYEES),
      switchMap((action) => {
        const params = action.payload;

        let serviceCall: Observable<PaginatedData<EmployeeListItem>>;

        switch (params.viewType) {
          case "EMPLOYEES":
            serviceCall = this.employeesService.getEmployees(params);
            break;
          case "ALIASES":
            serviceCall = this.employeesService.getEmployees(params, true);
            break;
          case "BOTH":
            serviceCall = this.employeesService.getCombinedEmployees(params);
            break;
          default:
            serviceCall = this.employeesService.getEmployees(params);
        }

        return serviceCall.pipe(
          map((employees) => new GetEmployeesSuccessAction(employees)),
          catchError((error) => of(new GetEmployeesFailureAction(error))),
        );
      }),
    ),
  );

  setEmployeeListParams$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_EMPLOYEES_PARAMS),
      map((action: SetEmployeesParamsAction) => action.payload),
      switchMap((payload: EmployeeListSearchParams) => [
        new SetLoadingAction(true),
        new GetEmployeesAction(payload),
      ]),
    ),
  );

  clearEmployeeListParams$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(CLEAR_EMPLOYEES_PARAMS),
      switchMap(() => [
        new SetLoadingAction(true),
        new SetEmployeesParamsAction({
          pager: { limit: 10, page: 0 },
          sortBy: "fullName",
          viewType: "EMPLOYEES",
        }),
      ]),
    ),
  );

  changeExcludedFromSignatures$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_EXCLUDED_FROM_SIGNATURES),
      map((action: ChangeExcludedFromSignaturesAction) => action.payload),
      switchMap((payload: ChangeExcludedFromSignaturesParams) => {
        return this.employeesService.changeExcludedFromSignatures(payload).pipe(
          switchMap(() => {
            return [
              new ChangeExcludedFromSignaturesSuccessAction({
                employeeId: payload.employeeId,
                excludedFromSignatures: payload.excludedFromSignatures,
              }),
            ];
          }),
          catchError((error) =>
            of(new ChangeExcludedFromSignaturesFailureAction(error)),
          ),
        );
      }),
    ),
  );

  excludeAllEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EXCLUDE_ALL_EMPLOYEES),
      switchMap((action: ExcludeAllEmployeesAction) => {
        return this.employeesService
          .excludeAllEmployees(action.payload.filters, action.payload.exclude)
          .pipe(
            switchMap(() => [
              new SetLoadingAction(true),
              new GetEmployeesAction(action.payload.filters),
            ]),
            catchError((error) => of(new GetEmployeesFailureAction(error))),
          );
      }),
    ),
  );

  sendEmployeeRequest$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(SEND_EMPLOYEE_REQUEST),
      map((action: SendEmployeeRequestAction) => action.payload),
      switchMap((payload: EmployeeRequest) => {
        return this.employeesService.sendEmployeeRequest(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("employee.sendRequest.success") as string,
            );
            return [new SendEmployeeRequestSuccessAction(response)];
          }),
          catchError((error) => {
            this.toastr.error(
              this.translate.instant("employee.sendRequest.error") as string,
            );
            return of(new SendEmployeeRequestFailureAction(error));
          }),
        );
      }),
    ),
  );

  updateEmployee$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(UPDATE_EMPLOYEE),
      map((action: UpdateEmployeeAction) => action.payload),
      switchMap((payload: Employee) => {
        if (
          (payload as Employee & { installSignature?: boolean })
            .installSignature
        ) {
          this.store.dispatch(
            new StartCheckingForInstallationInProgressAction(),
          );
        }
        return this.employeesService.updateEmployee(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("employee.saveSuccess") as string,
            );
            return [new UpdateEmployeeSuccessAction(response)];
          }),
          catchError((error) => of(new UpdateEmployeeFailureAction(error))),
        );
      }),
    ),
  );

  refreshListAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UPDATE_EMPLOYEE_SUCCESS),
      withLatestFrom(this.store.select(getEmployeesParamsSelector)),
      switchMap(([_, params]) => [
        new SetLoadingAction(true),
        new GetEmployeesAction(params),
      ]),
    ),
  );

  setLoadingFalseOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_EMPLOYEES_SUCCESS),
      map(() => new SetLoadingAction(false)),
    ),
  );

  setLoadingFalseOnFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_EMPLOYEES_FAILURE),
      map(() => new SetLoadingAction(false)),
    ),
  );

  getEmployee$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_EMPLOYEE),
      map((action: GetEmployeeAction) => action.payload),
      switchMap((payload: string) => {
        return this.employeesService.getEmployee(payload).pipe(
          switchMap((response: SingleEmployeeResponse) => {
            return [
              new GetEmployeeSuccessAction(response as unknown as Employee),
            ];
          }),
          catchError((error) => of(new GetEmployeeFailureAction(error))),
        );
      }),
    ),
  );

  getCurrentUser$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_CURRENT_USER),
      switchMap(() => {
        return this.employeesService.getCurrentUser().pipe(
          switchMap((response: CurrentUserResponse) => {
            if (response.email === this.authService.appUser?.email) {
              this.authService.changeAvatarUrl(
                (response as unknown as Employee).thumbnailPhotoUrl,
              );
            }
            return [
              new GetCurrentUserSuccessAction(response as unknown as Employee),
            ];
          }),
          catchError((error) => of(new GetCurrentUserFailureAction(error))),
        );
      }),
    ),
  );

  updateEmployeeAndInstallSignature$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(UPDATE_EMPLOYEE_AND_INSTALL_SIGNATURE),
      map((action: UpdateEmployeeAndInstallSignatureAction) => action.payload),
      switchMap((payload: Employee) => {
        this.store.dispatch(new StartCheckingForInstallationInProgressAction());
        return this.employeesService
          .updateEmployeeAndInstallSignature(payload)
          .pipe(
            switchMap((response) => {
              this.toastr.success(
                this.translate.instant(
                  "employee.saveAndInstallSuccess",
                ) as string,
              );
              return [
                new UpdateEmployeeAndInstallSignatureSuccessAction(response),
                new GetCurrentUserAction(),
              ];
            }),
            catchError((error) => {
              this.toastr.error(
                this.translate.instant("employee.saveError") as string,
              );
              return of(
                new UpdateEmployeeAndInstallSignatureFailureAction(error),
              );
            }),
          );
      }),
    ),
  );

  updateCurrentUser$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(UPDATE_CURRENT_USER),
      map((action: UpdateCurrentUserAction) => action.payload),
      switchMap((payload: Employee) => {
        return this.employeesService.updateCurrentUser(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("employee.saveSuccess") as string,
            );
            return [
              new UpdateCurrentUserSuccessAction(response),
              new GetCurrentUserAction(),
            ];
          }),
          catchError((error) => {
            this.toastr.error(
              this.translate.instant("employee.saveError") as string,
            );
            return of(new UpdateCurrentUserFailureAction(error));
          }),
        );
      }),
    ),
  );

  removeSignatureTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RemoveSignatureTemplateAction>(REMOVE_SIGNATURE_TEMPLATE),
      switchMap((action) =>
        this.employeesService
          .removeSignatureTemplate(
            action.payload.employeeId,
            action.payload.email,
            action.payload.isAlias || false,
            action.payload.internal || false,
          )
          .pipe(
            map(
              () =>
                new RemoveSignatureTemplateSuccessAction({
                  employeeId: action.payload.employeeId,
                  email: action.payload.email,
                  isAlias: action.payload.isAlias,
                  internal: action.payload.internal,
                }),
            ),
            catchError((error) =>
              of(new RemoveSignatureTemplateFailureAction(error)),
            ),
          ),
      ),
    ),
  );

  syncGoogleEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SYNC_GOOGLE_EMPLOYEES),
      switchMap(() => {
        return this.employeesService.syncGoogleEmployees().pipe(
          switchMap((result) => {
            if (!result) {
              this.toastr.error(
                this.translate.instant("employee.sync-googleError") as string,
              );
              return [
                new SyncGoogleEmployeesFailureAction({
                  message: "Unexpected null response",
                }),
              ];
            }

            if (!result.success && result.error) {
              return [
                new SyncGoogleEmployeesFailureAction({
                  ...result.error,
                  googleSyncStateId: result.googleSyncStateId,
                }),
              ];
            }

            return [
              new SyncGoogleEmployeesSuccessAction({
                googleSyncStateId: result.googleSyncStateId,
              }),
            ];
          }),
          catchError(() => {
            this.toastr.error(
              this.translate.instant("employee.sync-googleError") as string,
            );
            return of(
              new SyncGoogleEmployeesFailureAction({ type: "unknown_error" }),
            );
          }),
        );
      }),
    ),
  );

  syncMicrosoftEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SYNC_MICROSOFT_EMPLOYEES),
      switchMap(() => {
        return this.employeesService.syncMicrosoftEmployees().pipe(
          switchMap((result) => {
            if (!result) {
              this.toastr.error(
                this.translate.instant(
                  "employee.sync-microsoftError",
                ) as string,
              );
              return [
                new SyncMicrosoftEmployeesFailureAction({
                  message: "Unexpected null response",
                }),
              ];
            }

            if (!result.success && result.error) {
              return [
                new SyncMicrosoftEmployeesFailureAction({
                  ...result.error,
                  microsoftSyncStateId: result.microsoftSyncStateId,
                }),
              ];
            }

            return [
              new SyncMicrosoftEmployeesSuccessAction({
                microsoftSyncStateId: result.microsoftSyncStateId,
              }),
            ];
          }),
          catchError(() => {
            this.toastr.error(
              this.translate.instant("employee.sync-microsoftError") as string,
            );
            return of(
              new SyncMicrosoftEmployeesFailureAction({
                type: "unknown_error",
              }),
            );
          }),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private employeesService: EmployeesService,
    private authService: AuthService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private router: Router,
  ) {}
}
